/* eslint-disable import/no-extraneous-dependencies -- for adobe spectrum, aria, stately. */
import { Flex, Heading, Pill, Text, Truncate } from '@eleveo/components-react';
import { useButton } from '@react-aria/button';
import { FocusRing } from '@react-aria/focus';
import { PressEvent } from '@react-types/shared';
import clsx from 'clsx';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ETLConfiguration, ETLConfigurationType, ETL_INTEGRATION_TYPE, SUPPORTED_INTEGRATIONS } from './types';
import { TEST_IDS } from '../../constants/dataTestIds';
import { getPillConfig } from '../../utils/configurationCardUtils';
import { getIntegrationLogoAttributes, IntegrationLogoAttributes } from '../../utils/svgUtils';

// Make `integration` mandatory and other field optional without modifying original type.
type ConfigurationProp = Partial<ETLConfiguration> & Pick<ETLConfiguration, 'integration'>;

export interface ConfigurationCardProps<T extends ETLConfigurationType> {
  configuration: ConfigurationProp;
  isSmall?: boolean;
  onClick?: (configuration: T, e: PressEvent) => void;
  autoFocus?: boolean;
  testId?: string;
}

export const ConfigurationCard = <T extends ETLConfigurationType>({
  configuration,
  isSmall,
  onClick,
  autoFocus,
  testId,
}: ConfigurationCardProps<T>) => {
  const { integration, state, configurationName } = configuration;

  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement | null>(null);
  const { buttonProps } = useButton(
    {
      elementType: 'div',
      onPress: (e) => onClick && onClick(configuration as T, e),
    },
    ref
  );

  const [logoAttributes, setLogoAttributes] = useState<IntegrationLogoAttributes | undefined>(undefined);

  useEffect(
    () => {
      getIntegrationLogoAttributes(integration, t).then(setLogoAttributes);
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [integration]
  );

  const isTextBlack = useMemo(
    () => ([ETL_INTEGRATION_TYPE.ucce, ETL_INTEGRATION_TYPE.uccx] as string[]).includes(integration.toLowerCase()),
    [integration]
  );

  const pillConfig = useMemo(
    () => getPillConfig(state, t),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state]
  );

  const providerKey = useMemo(() => {
    const integrationToCompare = String(integration).toLowerCase();
    if ((SUPPORTED_INTEGRATIONS as readonly string[]).includes(integrationToCompare)) {
      return integrationToCompare;
    }

    return ETL_INTEGRATION_TYPE.unknown;
  }, [integration]);

  return (
    <FocusRing focusRingClass="configuration-card--focused" autoFocus={!!autoFocus}>
      <div
        className={clsx(
          'configuration-card',
          'flex',
          'flex-col',
          'rounded',
          'relative',
          'shadow',
          'overflow-hidden',
          'flex-shrink-0',
          `configuration-card--${providerKey}`,
          {
            'configuration-card--small': isSmall,
          }
        )}
        {...buttonProps}
        data-test-id={testId}
        ref={ref}
      >
        {pillConfig && (
          <Pill
            color={pillConfig.color}
            variant={pillConfig.variant}
            className={clsx('configuration-card__pill', 'absolute')}
            testId={TEST_IDS.pillCard}
          >
            {pillConfig.icon}
            {pillConfig.statusText}
          </Pill>
        )}
        <Flex justifyContent="center" alignItems="center" className="configuration-card__image-wrapper">
          {logoAttributes && (
            <img src={logoAttributes.image} alt={logoAttributes.alt} className={logoAttributes.className} />
          )}
        </Flex>
        <Flex
          justifyContent="center"
          alignItems="center"
          column
          className={clsx(
            'configuration-card__text',
            `configuration-card__text--${providerKey}`,
            'text-center',
            'flex-grow',
            {
              // in order to display ellipsis (dots) color correctly we need to set text color of parent element
              'text-black': isTextBlack,
              'text-white': !isTextBlack,
            }
          )}
        >
          <Heading as="h3" className="text-white">
            {t(`providers.${providerKey}`)}
          </Heading>
          {configurationName && (
            <Truncate>
              <Text
                className={clsx('font-bold', {
                  'text-black': isTextBlack,
                  'text-white': !isTextBlack,
                })}
              >
                {configurationName}
              </Text>
            </Truncate>
          )}
        </Flex>
      </div>
    </FocusRing>
  );
};
